import React, { useCallback, useEffect, useState } from "react";
import "devextreme/dist/css/dx.light.css";
import DataGrid, { Column, LoadPanel } from "devextreme-react/data-grid";
import PopUp from "devextreme-react/popup";
import { Popover } from "devextreme-react/popover";
import { Button } from "devextreme-react/button";
import {
  ResourceActionSVG,
  ResourceSubscribeSVG,
  NullDataGridSVG,
  ResourceUnsubscribeSVG,
} from "../../Assets/SVGs/ResourceSVGs";
import { Roles, format, unSubscribeRes } from "../../Constants/constants";
import Notification from "../Notification/Notification";
import { resourceGridStatus } from "../DatagridCells/DatagridCells";
import { useUser } from "../../contexts/UserContext";
import "./SubscriptionTab.scss";
import TimeElapseWithTooltip from "../TimeElapseWithTooltip/TimeElapseWithTooltip";
import SubscribeResourceToMultipleOrgs from "../SubscribeOrganizations/SubscribeResourceToMultipleOrgs";
import Pagination from "../Pagination/Pagination";
import { getResourceTypeDisplayName } from "../../utilities";
import { orgDataPropTypes } from "../../Constants/propTypes";

const subscriptionResourceView = "View and Select your organization's subscriptions";

const SubscriptionTab = ({ orgData }) => {
  const { userRoles, backend } = useUser();
  const [isPopOver, setIsPopOver] = useState(false);
  const [allResources, setAllResources] = useState([{}]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [checkResourceType, setCheckResourceType] = useState("");
  const [resourceId, setResourceId] = useState();
  const [resourceVersionId, setResourceVersionId] = useState("");
  const [popupResourceName, setPopupResourceName] = useState("");
  const [isUnsubscribePopupVisible, setIsUnsubscribePopupVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [allResourcesCount, setAllResourcesCount] = useState(0);

  const resourceType = null;
  const resourceName = "";

  const getResourcesWithSubDetails = useCallback(() => {
    const allResDetailsObj = {
      pageNumber,
      pageSize: 10,
      resourceType,
      resourceName,
    };
    backend
      .callSubscriptionTabResourceList(orgData?.id, allResDetailsObj)
      .then((res) => {
        setAllResources(res?.data?.resourcesWithSubscriptions);
        setAllResourcesCount(res?.data?.totalCount);
      })
      .catch(() => {
        setAllResources(null);
      });
  }, [backend, orgData?.id, pageNumber]);

  useEffect(() => {
    setCurrentPage(1);
    setPageNumber(1);
  }, [orgData]);

  useEffect(() => {
    getResourcesWithSubDetails();
  }, [orgData?.id, backend, pageNumber, getResourcesWithSubDetails]);

  const pageChanged = useCallback((page) => {
    setCurrentPage(page);
    setPageNumber(page);
  }, []);

  const unSubscribeResource = () => {
    setIsUnsubscribePopupVisible(false);
    setIsLoading(true);
    const unSubscribeResDetailsObj = {
      organizationId: orgData?.id,
      resourceVersionId,
    };
    backend
      .callUnSubscribeResourceVersion(unSubscribeResDetailsObj)
      .then((res) => {
        if (res.succeeded) {
          Notification(res.Message || res.message, "success");
          setIsPopOver(false);
          getResourcesWithSubDetails();
        } else {
          Notification(res.Message || res.message, "error");
          setIsPopOver(false);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Something went wrong. Please try again later", error);
      });
  };
  const renderPopupContent = () => (
    <div>
      <div className="text-align-center">
        <span className="subscriptionTabUnSubscribe">
          {unSubscribeRes?.unSubscribeQues}
        </span>
        <div>
          <span className="subscriptionTabUnSubscribeText">
            {unSubscribeRes?.unSubscribeText}
          </span>
        </div>
        <hr className="horizontalLine" />
        <div className="cancel-button-resource">
          <Button
            className="btn-outlined"
            onClick={() => setIsUnsubscribePopupVisible(false)}
            text="Cancel"
          />
        </div>
        <div className="resource-button">
          <Button
            className="btn-default"
            onClick={unSubscribeResource}
            text="Yes, unsubscribe"
          />
        </div>
      </div>
    </div>
  );

  const subscribePopupHidden = () => {
    setIsUnsubscribePopupVisible(false);
    setIsPopOver(false);
  };

  const popupHidden = useCallback(() => {
    setIsPopupVisible(false);
  }, []);

  const calculateRemainingDays = (endDate) => {
    if (!endDate) return null;

    const today = new Date();
    const givenDate = new Date(endDate);

    if (Number.isNaN(today.getTime()) || Number.isNaN(givenDate.getTime())) {
      return null;
    }

    // Normalize both dates to start of the day
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const givenDateStart = new Date(
      givenDate.getFullYear(),
      givenDate.getMonth(),
      givenDate.getDate()
    );

    // Calculate difference in days
    const differenceInMs = givenDateStart - todayStart;
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor(differenceInMs / millisecondsPerDay);
  };

  const dateCell = (data, isCellEndDate) => {
    const resourceEndDate = data?.data?.resourceSubscription?.subscriptionEnd;
    const remainingDays = calculateRemainingDays(resourceEndDate);

    const renderContent = () => {
      if (remainingDays >= 0 && remainingDays <= 30) {
        if (
          data?.data?.resourceSubscription?.subscriptionStart &&
          data?.data?.resourceSubscription?.subscriptionEnd
        ) {
          return (
            <TimeElapseWithTooltip
              daysLeft={remainingDays + 1}
              id={data?.data?.resource?.id}
            />
          );
        }
      } else if (remainingDays > 30) {
        return null;
      }
      return <TimeElapseWithTooltip id={data?.data?.resource?.id} />;
    };

    return data?.value ? (
      <div style={{ display: "flex" }}>
        {data?.text}
        {isCellEndDate ? renderContent() : null}
      </div>
    ) : (
      <span>{NullDataGridSVG}</span>
    );
  };

  const handleAction = useCallback((cellData) => {
    setIsPopOver(true);
    setResourceId(cellData?.resource?.id);
    setPopupResourceName(cellData?.resource?.name);
    setResourceVersionId(cellData?.resourceVersion?.id);
    if (cellData?.resourceSubscription) {
      const daysLeft = calculateRemainingDays(
        cellData?.resourceSubscription?.subscriptionEnd
      );
      if (daysLeft > -1) {
        setCheckResourceType("subscribed");
      } else if (daysLeft < 0) {
        setCheckResourceType("expired");
      } else {
        setCheckResourceType("available");
      }
    } else {
      setCheckResourceType("available");
    }
  }, []);

  const renderActionCell = useCallback(
    (cellData) =>
      cellData?.resourceVersion?.active ? (
        <span className="actionCol">
          <Button
            id={`buttonId-${cellData?.resource?.id}`}
            onClick={() => {
              handleAction(cellData);
            }}
            stylingMode="text"
          >
            {ResourceActionSVG}
          </Button>
        </span>
      ) : (
        <span className="actionColdeprecate">{ResourceActionSVG}</span>
      ),
    [handleAction]
  );

  const onClickUnsubscribe = () => {
    setIsUnsubscribePopupVisible(true);
    setIsPopOver(false);
  };
  const onClickSubscribe = () => {
    setIsPopupVisible(true);
    setIsPopOver(false);
  };

  const actionsPopup = (svg, text, index, onClickActionResource = () => {}) => (
    <Button
      className="sub-span"
      onClick={onClickActionResource}
      stylingMode="text"
      width="100%"
      height="100%"
      key={index}
    >
      <span className="popover-icons">{svg}</span>
      <span className="unsubscribe-text">{text}</span>
    </Button>
  );
  const availableResPopup = [
    {
      svg: ResourceSubscribeSVG,
      text: "Subscribe customers",
      onClickActionResource: onClickSubscribe,
    },
  ];
  const SubscribeResPopup = [
    {
      svg: ResourceUnsubscribeSVG,
      text: "Unsubscribe",
      onClickActionResource: onClickUnsubscribe,
    },
    {
      svg: ResourceSubscribeSVG,
      text: "Subscribe customers",
      onClickActionResource: onClickSubscribe,
    },
  ];

  const expiredResourcePopup = [
    {
      svg: ResourceUnsubscribeSVG,
      text: "Unsubscribe",
      onClickActionResource: onClickUnsubscribe,
    },
  ];

  const actionPopoverDetails = (arr) =>
    arr?.map((item, index) =>
      actionsPopup(item?.svg, item?.text, index, item?.onClickActionResource)
    );

  const renderActionPopover = () => {
    if (checkResourceType === "available") {
      return actionPopoverDetails(availableResPopup);
    }
    if (checkResourceType === "subscribed") {
      return actionPopoverDetails(SubscribeResPopup);
    }
    if (checkResourceType === "expired") {
      return actionPopoverDetails(expiredResourcePopup);
    }
    return null;
  };

  return (
    <div>
      <div className="resources-header-texts">{subscriptionResourceView}</div>
      <div className="p-3">
        <Popover
          visible={isPopOver}
          width={150}
          target={`#buttonId-${resourceId}`}
          position="bottom"
          onHiding={() => {
            setIsPopOver(!isPopOver);
          }}
        >
          <div className="popover-styles">{renderActionPopover()}</div>
        </Popover>
        <LoadPanel visible={isLoading} />
        <PopUp
          visible={isUnsubscribePopupVisible}
          hideOnOutsideClick={true}
          onHidden={subscribePopupHidden}
          showTitle={true}
          title="Unsubscribe resource"
          position="center"
          maxHeight={250}
          maxWidth={430}
          contentRender={renderPopupContent}
        />
        <SubscribeResourceToMultipleOrgs
          visible={isPopupVisible}
          onHidingCallback={popupHidden}
          resourceVersionDisplay={`${popupResourceName} - Version ${resourceVersionId}`}
          resourceVersionId={resourceVersionId}
          isInfoButtonVisible={true}
          clinithinkChildOrg={false}
          orgData={orgData}
          onSubscriptionSuccessCallback={getResourcesWithSubDetails}
          checkResourcesType={checkResourceType}
        />
        <div id="subscriptiontab-div-gridwrapper" className="datagrid-available-res">
          <DataGrid
            columnAutoWidth={true}
            allowColumnResizing={true}
            wordWrapEnabled={true}
            container="#subscriptiontab-div-gridwrapper"
            dataSource={allResources}
            hoverStateEnabled={true}
            showBorders={true}
            noDataText="No resources available"
          >
            <LoadPanel visible={true} />
            <Column dataField="resource.name" caption="Resource name" />
            <Column
              dataField="resource.resourceTypeId"
              caption="Resource type"
              customizeText={({ value }) =>
                getResourceTypeDisplayName(value?.toUpperCase())
              }
            />
            <Column dataField="resourceVersion.versionNumber" caption="Version" />
            <Column
              caption="Start date"
              dataField="resourceSubscription.subscriptionStart"
              dataType="date"
              format={format.dateFormat}
              cellRender={(data) => dateCell(data, false)}
            />
            <Column
              dataType="date"
              dataField="resourceSubscription.subscriptionEnd"
              format={format.dateFormat}
              caption="End date"
              cellRender={(data) => dateCell(data, true)}
            />
            <Column
              dataField="resourceVersion.active"
              caption="Status"
              dataType="string"
              cellRender={resourceGridStatus}
            />
            {userRoles?.includes(Roles?.Resource_Manager || Roles?.Resource_Read) && (
              <Column
                caption="Actions"
                type="buttons"
                name="resViewButton"
                width={90}
                cellRender={(cellData) => renderActionCell(cellData?.data)}
              />
            )}
          </DataGrid>
        </div>
        {allResources && (
          <div>
            <Pagination
              className="pagination"
              currentPage={currentPage}
              totalCount={allResourcesCount}
              pageSize={10}
              onPageChange={(page) => pageChanged(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
SubscriptionTab.propTypes = {
  orgData: orgDataPropTypes.isRequired,
};
export default SubscriptionTab;
